import React from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import Chip from '@/components/General/Chip';

/**
 * Compact card rendering a get_profile_view_card RPC payload:
 *   { view: {id, name, config}, person: {...}, sections: {...} }
 * Section keys are omitted server-side when not configured or not permitted —
 * the card simply renders whichever keys are present.
 */

const RANGE_LABELS = {
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    '365d': 'Last 365 days',
    'year': 'This year',
    'all': 'All time',
};

const rangeLabel = (range, explicit) => explicit || RANGE_LABELS[range] || range || null;

const formatDate = (value) => {
    if (!value) return null;
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : String(value);
};

const formatAddress = (address) => {
    if (!address) return null;
    if (typeof address === 'string') return address;
    return [address.street, address.streetLine2, [address.city, address.state].filter(Boolean).join(', '), address.zip]
        .filter(Boolean)
        .join(' ');
};

const formatPhone = (phone) => {
    if (!phone) return null;
    if (typeof phone === 'string') return phone;
    return phone.phoneNumber || phone.number || phone.phone || phone.value || null;
};

const formatCustomFieldValue = (value, type) => {
    if (value === null || value === undefined || value === '') return null;
    if (type === 'Checkbox' || typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (type === 'Date' || type === 'Datetime') return formatDate(value);
    if (Array.isArray(value)) return value.map(formatCustomFieldValue).filter(Boolean).join(', ');
    if (typeof value === 'object') return value.name || value.label || null;
    return String(value);
};

const SectionLabel = ({ icon, label, colorVariant = 'textSecondary' }) => (
    <View style={styles.sectionLabelRow}>
        <Unicon name={icon} size={14} colorVariant={colorVariant} />
        <Text style={styles.sectionLabel(colorVariant)}>{label}</Text>
    </View>
);

const FieldRow = ({ icon, text }) => (
    <View style={styles.fieldRow}>
        {!!icon && <Unicon name={icon} size={14} colorVariant="textTertiary" />}
        <Text style={text ? styles.fieldText : styles.emptyValue} numberOfLines={2}>{text || 'Empty'}</Text>
    </View>
);

const CustomFieldRow = ({ label, value }) => (
    <View style={styles.customFieldRow}>
        <Text style={styles.customFieldLabel} numberOfLines={1}>{label}</Text>
        <Text style={value ? styles.customFieldValue : styles.emptyValue} numberOfLines={2}>{value || 'Empty'}</Text>
    </View>
);

const StatBox = ({ icon, colorVariant, label, value, sublabel, testID }) => (
    <View style={styles.statBox(colorVariant)} testID={testID}>
        <View style={styles.statHeader}>
            <Unicon name={icon} size={14} colorVariant={colorVariant} />
            <Text style={styles.statLabel(colorVariant)}>{label}</Text>
        </View>
        <Text style={styles.statValue}>{value}</Text>
        {!!sublabel && <Text style={styles.statSublabel}>{sublabel}</Text>}
    </View>
);

const ProfileViewCard = ({ data, testID = 'ProfileViewCard' }) => {
    const { view, person, sections } = data || {};
    const config = view?.config || {};
    const personName = [person?.firstName, person?.lastName].filter(Boolean).join(' ') || person?.name || 'Unknown';

    const contact = sections?.contact;
    const basic = sections?.basic;
    const customFields = Array.isArray(sections?.customFields) ? sections.customFields : [];
    const attendance = sections?.attendance;
    const tasks = sections?.tasks;
    const roles = Array.isArray(sections?.roles) ? sections.roles : [];
    const training = Array.isArray(sections?.training) ? sections.training : [];
    const smallGroups = Array.isArray(sections?.smallGroups) ? sections.smallGroups : null;
    const familyObj = sections?.family;
    const familyMembers = Array.isArray(familyObj?.members) ? familyObj.members : [];

    // Configured fields render even when empty — section/key presence in the
    // RPC payload marks the field as configured for this view.
    const contactRows = [];
    if (contact) {
        if ('email' in contact) contactRows.push({ icon: 'mail-outline', text: contact.email || '', key: 'email' });
        if ('phones' in contact) {
            const phones = Array.isArray(contact.phones) ? contact.phones : contact.phones ? [contact.phones] : [];
            if (phones.length === 0) contactRows.push({ icon: 'call-outline', text: '', key: 'phone-empty' });
            phones.forEach((phone, i) => contactRows.push({ icon: 'call-outline', text: formatPhone(phone) || '', key: 'phone-' + i }));
        }
        if ('address' in contact) contactRows.push({ icon: 'location-outline', text: formatAddress(contact.address) || '', key: 'address' });
    }

    const basicRows = [];
    if (basic) {
        if ('dateOfBirth' in basic || 'birthDate' in basic) {
            const dobValue = basic.dateOfBirth || basic.birthDate;
            const dob = DateTime.fromISO(dobValue || '');
            const age = dob.isValid ? Math.floor(DateTime.now().diff(dob, 'years').years) : null;
            const birthday = formatDate(dobValue);
            basicRows.push({ icon: 'gift-outline', text: birthday ? `${birthday}${age !== null ? ` (${age})` : ''}` : '', key: 'dob' });
        }
        if ('gender' in basic) basicRows.push({ icon: 'person-outline', text: basic.gender || '', key: 'gender' });
    }

    const attendanceCount = attendance ? (attendance.timesAttended ?? attendance.count ?? attendance.attended ?? attendance.total ?? 0) : 0;
    const tasksAssigned = tasks ? (tasks.assigned ?? tasks.total ?? 0) : 0;
    const tasksCompleted = tasks ? (tasks.completed ?? 0) : 0;

    const hasSection = !!contact || !!basic || customFields.length > 0 ||
        !!attendance || !!tasks || roles.length > 0 || training.length > 0 || !!smallGroups || !!familyObj;

    return (
        <View style={styles.card} testID={testID}>
            {/* Header — name always shows; avatar per config */}
            <View style={styles.header}>
                {config.avatar !== false && !!person && (
                    <Avatar size={46} profile={person} disableBorder />
                )}
                <View style={styles.headerInfo}>
                    <Text style={styles.personName} numberOfLines={1} testID={testID + '-name'}>{personName}</Text>
                    {!!view?.name && <Text style={styles.viewName} numberOfLines={1}>{view.name}</Text>}
                </View>
            </View>

            {!hasSection && (
                <Text style={styles.emptyNote}>No details to show for this view.</Text>
            )}

            {!!contact && (
                <View style={styles.section} testID={testID + '-section-contact'}>
                    <SectionLabel icon="chatbox-ellipses-outline" label="Contact" colorVariant="people" />
                    {contactRows.map(row => <FieldRow key={row.key} icon={row.icon} text={row.text} />)}
                </View>
            )}

            {!!basic && (
                <View style={styles.section} testID={testID + '-section-basic'}>
                    <SectionLabel icon="information-circle-outline" label="Basic Info" colorVariant="people" />
                    {basicRows.map(row => <FieldRow key={row.key} icon={row.icon} text={row.text} />)}
                </View>
            )}

            {customFields.length > 0 && (
                <View style={styles.section} testID={testID + '-section-customFields'}>
                    <SectionLabel icon="reader-outline" label="Details" colorVariant="people" />
                    {customFields.map((field, i) => {
                        const value = formatCustomFieldValue(field.value ?? field.displayValue, field.type) || '';
                        return <CustomFieldRow key={field.id || field.name || i} label={field.name || field.label || 'Field'} value={value} />;
                    })}
                </View>
            )}

            {(attendance || tasks) && (
                <View style={styles.statsRow}>
                    {!!attendance && (
                        <StatBox
                            testID={testID + '-stat-attendance'}
                            icon="calendar-outline"
                            colorVariant="events"
                            label="Attendance"
                            value={`${attendanceCount}`}
                            sublabel={rangeLabel(attendance.range, attendance.rangeLabel)}
                        />
                    )}
                    {!!tasks && (
                        <StatBox
                            testID={testID + '-stat-tasks'}
                            icon="checkbox-outline"
                            colorVariant="tasks"
                            label="Tasks"
                            value={`${tasksCompleted}/${tasksAssigned}`}
                            sublabel={rangeLabel(tasks.range, tasks.rangeLabel) || 'completed'}
                        />
                    )}
                </View>
            )}

            {roles.length > 0 && (
                <View style={styles.section} testID={testID + '-section-roles'}>
                    <SectionLabel icon="shield-checkmark-outline" label="Roles" />
                    {roles.map((role, i) => {
                        const held = role.member ?? role.assigned ?? role.hasRole ?? true;
                        return (
                            <View key={role.id || role.name || i} style={styles.roleRow}>
                                <Text style={styles.roleName(held)} numberOfLines={1}>{role.name || 'Role'}</Text>
                                <Unicon name={held ? 'checkmark-circle' : 'remove'} size={16} colorVariant={held ? 'success' : 'textTertiary'} />
                            </View>
                        );
                    })}
                </View>
            )}

            {training.length > 0 && (
                <View style={styles.section} testID={testID + '-section-training'}>
                    <SectionLabel icon="school-outline" label="Training" colorVariant="training" />
                    {training.map((module, i) => {
                        const completed = module.classesCompleted ?? 0;
                        const total = module.totalClasses ?? module.classesTotal ?? 0;
                        const pct = module.completionPercent ?? (total > 0 ? Math.round((completed / total) * 100) : 0);
                        return (
                            <View key={module.moduleID || module.id || module.moduleName || i} style={styles.trainingRow}>
                                <Text style={styles.trainingName} numberOfLines={1}>{module.moduleName || module.name || module.title || 'Module'}</Text>
                                <Text style={styles.trainingProgress}>{completed}/{total} · {pct}%</Text>
                            </View>
                        );
                    })}
                </View>
            )}

            {!!smallGroups && (
                <View style={styles.section} testID={testID + '-section-smallGroups'}>
                    <SectionLabel icon="people-circle-outline" label="Small Groups" colorVariant="groups" />
                    {smallGroups.length === 0 ? (
                        <Text style={styles.emptyValue}>Empty</Text>
                    ) : (
                        <View style={styles.chipsWrap}>
                            {smallGroups.map((group, i) => (
                                <Chip key={group.id || group.name || i} colorVariant="groups">{group.name || 'Group'}</Chip>
                            ))}
                        </View>
                    )}
                </View>
            )}

            {!!familyObj && (
                <View style={styles.section} testID={testID + '-section-family'}>
                    <SectionLabel icon="home-outline" label="Family" colorVariant="people" />
                    {familyMembers.length === 0 && <Text style={styles.emptyValue}>Empty</Text>}
                    {familyMembers.map((member, i) => {
                        const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || member.name || 'Member';
                        return (
                            <View key={member.id || i} style={styles.familyRow}>
                                {!!member && <Avatar size={30} profile={member} disableBorder />}
                                <Text style={styles.familyName} numberOfLines={1}>{name}</Text>
                                {!!(member.familyRole || member.role) && (
                                    <Text style={styles.familyRole} numberOfLines={1}>{member.familyRole || member.role}</Text>
                                )}
                            </View>
                        );
                    })}
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography, roundness }) => ({
    card: {
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        boxShadow: shadows.card,
        gap: spacing.sm + 2,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm + 4,
    },
    headerInfo: {
        flex: 1,
        minWidth: 0,
        gap: 1,
    },
    personName: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
        flexShrink: 1,
    },
    viewName: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
    emptyNote: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        fontStyle: 'italic',
    },
    emptyValue: {
        flex: 1,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        fontStyle: 'italic',
    },
    section: {
        gap: spacing.xs + 1,
        paddingTop: spacing.sm,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: colors.borderSubtle,
    },
    sectionLabelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs + 2,
    },
    sectionLabel: (colorVariant) => ({
        fontSize: typography.micro,
        fontFamily: fonts.heading,
        color: colors[colorVariant] || colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    }),
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs + 2,
    },
    fieldText: {
        flex: 1,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.text,
    },
    customFieldRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: spacing.sm,
    },
    customFieldLabel: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        flexShrink: 1,
    },
    customFieldValue: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.text,
        flexShrink: 1,
        textAlign: 'right',
    },
    statsRow: {
        flexDirection: 'row',
        gap: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: StyleSheet.hairlineWidth,
        borderTopColor: colors.borderSubtle,
    },
    statBox: (colorVariant) => ({
        flex: 1,
        borderRadius: roundness.sm,
        backgroundColor: colors[`${colorVariant}Container`] || colors.surfaceVariant,
        padding: spacing.sm + 2,
        gap: 2,
    }),
    statHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
    },
    statLabel: (colorVariant) => ({
        fontSize: typography.micro,
        fontFamily: fonts.heading,
        color: colors[colorVariant] || colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    }),
    statValue: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    statSublabel: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    roleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.sm,
    },
    roleName: (held) => ({
        flex: 1,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: held ? colors.text : colors.textTertiary,
    }),
    trainingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.sm,
    },
    trainingName: {
        flex: 1,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.text,
    },
    trainingProgress: {
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
    },
    chipsWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.xs + 2,
    },
    familyRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    familyName: {
        flex: 1,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.text,
    },
    familyRole: {
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
}));

export default React.memo(ProfileViewCard);
